<template>
  <div class="join-page">
    <div class="join-card">
      <!-- Loading state -->
      <div v-if="loading" class="loading-state">
        <p>Loading invite details...</p>
      </div>

      <!-- Error state -->
      <div v-else-if="error" class="error-state">
        <h2>Unable to Join</h2>
        <p>{{ error }}</p>
        <router-link to="/dashboard" class="btn-home">Go to Dashboard</router-link>
      </div>

      <!-- Success state -->
      <div v-else-if="joined" class="success-state">
        <h2>Welcome to the Family!</h2>
        <p>You've successfully joined <strong>{{ inviteInfo.household_name }}</strong>.</p>
        <p>You can now see shared receipts from other household members.</p>
        <router-link to="/dashboard" class="btn-home">Go to Dashboard</router-link>
      </div>

      <!-- Invite preview (not logged in) -->
      <div v-else-if="!isAuthenticated" class="invite-preview">
        <h2>You're Invited!</h2>
        <p><strong>{{ inviteInfo.owner_name }}</strong> has invited you to join their household on Budget Buddy.</p>

        <div class="household-preview">
          <span class="household-name">{{ inviteInfo.household_name }}</span>
        </div>

        <p class="login-prompt">Sign in with Google to accept this invitation.</p>

        <button @click="loginAndAccept" class="btn-login">
          Sign in with Google
        </button>

        <p class="expires-note">
          This invite expires on {{ formatDate(inviteInfo.expires_at) }}
        </p>
      </div>

      <!-- Accept invite (logged in) -->
      <div v-else class="accept-invite">
        <h2>Join Household?</h2>
        <p><strong>{{ inviteInfo.owner_name }}</strong> has invited you to join their household.</p>

        <div class="household-preview">
          <span class="household-name">{{ inviteInfo.household_name }}</span>
        </div>

        <div class="invite-info">
          <p>By joining, you will:</p>
          <ul>
            <li>Share your receipts with household members</li>
            <li>See receipts from other members who share</li>
            <li>Use the household's Monarch Money connection</li>
          </ul>
        </div>

        <div class="action-buttons">
          <button @click="acceptInvite" :disabled="accepting" class="btn-accept">
            {{ accepting ? 'Joining...' : 'Join Household' }}
          </button>
          <router-link to="/dashboard" class="btn-decline">Decline</router-link>
        </div>

        <p class="expires-note">
          This invite expires on {{ formatDate(inviteInfo.expires_at) }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '../store/auth'
import { householdAPI, authAPI } from '../services/api'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const loading = ref(true)
const error = ref(null)
const inviteInfo = ref({})
const accepting = ref(false)
const joined = ref(false)

const isAuthenticated = computed(() => authStore.isAuthenticated)

onMounted(async () => {
  await loadInviteInfo()
})

const loadInviteInfo = async () => {
  loading.value = true
  error.value = null

  const token = route.params.token

  if (!token) {
    error.value = 'Invalid invite link'
    loading.value = false
    return
  }

  try {
    const response = await householdAPI.getInviteInfo(token)
    inviteInfo.value = response.data
  } catch (err) {
    if (err.response?.status === 404) {
      error.value = 'This invite link is invalid or has been revoked.'
    } else if (err.response?.status === 410) {
      error.value = err.response?.data?.error || 'This invite has expired.'
    } else {
      error.value = 'Failed to load invite details. Please try again.'
    }
  } finally {
    loading.value = false
  }
}

const loginAndAccept = async () => {
  // Store the invite token in localStorage so we can accept after login
  localStorage.setItem('pendingInviteToken', route.params.token)

  // Redirect to login
  try {
    const response = await authAPI.login()
    window.location.href = response.data.authorization_url
  } catch (err) {
    error.value = 'Failed to initiate login. Please try again.'
  }
}

const acceptInvite = async () => {
  accepting.value = true
  error.value = null

  try {
    await householdAPI.acceptInvite(route.params.token)
    joined.value = true
  } catch (err) {
    error.value = err.response?.data?.error || 'Failed to join household. Please try again.'
  } finally {
    accepting.value = false
  }
}

const formatDate = (isoString) => {
  if (!isoString) return ''
  const date = new Date(isoString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}
</script>

<style scoped>
.join-page {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80vh;
  padding: 1rem;
}

.join-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  max-width: 450px;
  width: 100%;
  text-align: center;
}

.join-card h2 {
  color: #2c3e50;
  margin-bottom: 1rem;
}

.join-card p {
  color: #7f8c8d;
  margin-bottom: 1rem;
  line-height: 1.5;
}

.loading-state {
  padding: 2rem 0;
  color: #7f8c8d;
}

.error-state {
  padding: 1rem 0;
}

.error-state h2 {
  color: #e74c3c;
}

.success-state h2 {
  color: #27ae60;
}

.household-preview {
  background-color: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 1rem;
  margin: 1.5rem 0;
}

.household-name {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2c3e50;
}

.invite-info {
  text-align: left;
  background-color: #f8f9fa;
  border-radius: 6px;
  padding: 1rem;
  margin: 1.5rem 0;
}

.invite-info p {
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.invite-info ul {
  margin: 0;
  padding-left: 1.25rem;
  color: #7f8c8d;
}

.invite-info li {
  margin: 0.5rem 0;
}

.login-prompt {
  margin-top: 1.5rem;
  font-weight: 500;
  color: #2c3e50;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1.5rem;
}

.btn-login,
.btn-accept {
  background-color: #27ae60;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-login:hover,
.btn-accept:hover {
  background-color: #219a52;
}

.btn-accept:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-decline {
  color: #7f8c8d;
  text-decoration: none;
  padding: 0.5rem;
}

.btn-decline:hover {
  color: #2c3e50;
}

.btn-home {
  display: inline-block;
  margin-top: 1rem;
  background-color: #3498db;
  color: white;
  text-decoration: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 1rem;
  transition: background-color 0.2s;
}

.btn-home:hover {
  background-color: #2980b9;
}

.expires-note {
  margin-top: 1.5rem;
  font-size: 0.85rem;
  color: #bdc3c7;
}
</style>
